import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@nohara-dxdev/react-core';
import axios, { AxiosProgressEvent } from 'axios';

import { ReceiptDocumentItem } from '../types/property.types';

import { GetPropertiesParams, GetPropertiesResponse } from './types';

import { apiClient } from '@/axios/app-client';

/**
 * Fetch properties from the API
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns Promise with properties response
 */
export const getProperties = async (
  params?: GetPropertiesParams
): Promise<GetPropertiesResponse> => {
  const { data } = await apiClient.get('/v1/properties', {
    params,
  });
  return data.data;
};

/**
 * Query options for fetching properties
 * @param params - Query parameters
 * @returns Query options for React Query
 */
export const getPropertiesQueryOptions = (params?: GetPropertiesParams) => {
  return queryOptions({
    queryKey: ['properties', params],
    queryFn: () => getProperties(params),
  });
};

/**
 * Hook to fetch properties using React Query
 * @param params - Query parameters
 * @param config - Additional query configuration
 * @returns Query result with properties data
 */
export const useGetProperties = (
  params?: GetPropertiesParams,
  config?: QueryConfig<typeof getPropertiesQueryOptions>
) => {
  return useQuery({
    ...getPropertiesQueryOptions(params),
    refetchOnMount: 'always',
    staleTime: 0, // Always consider data stale to ensure fresh data on every request
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new data for better UX
    ...config,
  });
};

export const getPresignedUrl = async (
  propertyId: string,
  documentName: string,
  operation: 'upload' | 'download'
) => {
  try {
    const { data } = await apiClient.get(`/v1/properties/${propertyId}/presigned`, {
      params: {
        documentName,
        operation,
      },
    });
    return data.data;
  } catch {
    return {
      url: '',
    };
  }
};

export const uploadToS3 = async (
  presignedUrl: string,
  file: File,
  onProgress?: (progress: number) => void
) => {
  return new Promise((resolve, reject) => {
    axios
      .put(presignedUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent: AxiosProgressEvent) => {
          if (globalThis.window !== undefined && progressEvent.total) {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            // You can do something with the progress, for now let's just log it
            // If you want to notify external UI, accept a callback argument and call it here
            if (onProgress) {
              onProgress(percentCompleted);
            }
          }
        },
      })
      .then((result) => {
        resolve(result);
      })
      .catch((error) => {
        // Ensure error is an Error instance
        reject(error instanceof Error ? error : new Error(String(error)));
      });
  });
};
/**
 * Delete a receipt document
 * @param propertyId - The property ID
 * @param documentId - The document ID
 * @returns The receipt document
 */
export const deleteReceiptDocument = async (propertyId: string, documentId: string) => {
  const { data } = await apiClient.delete(
    `/v1/properties/${propertyId}/receipt-documents/${documentId}`
  );
  return data.data;
};

/**
 * Get initial receipt documents
 * @param propertyId - The property ID
 * @returns The initial receipt documents
 */
export const getInitialReceiptDocuments = async (propertyId: string) => {
  const { data } = await apiClient.get(`/v1/properties/${propertyId}/receipt-documents`);
  return data.data;
};

/**
 * Query options for fetching initial receipt documents
 * @param propertyId - The property ID
 * @param receiptDocuments - The receipt documents
 * @returns Query options for React Query
 */
export const addReceiptDocuments = async (
  propertyId: string,
  receiptDocuments: ReceiptDocumentItem[]
) => {
  const { data } = await apiClient.post(
    `/v1/properties/${propertyId}/receipt-documents`,
    receiptDocuments
  );
  return data.data;
};

/**
 * Download a receipt document
 * @param file - The file to download
 * @param fileName - The file name
 * @returns The receipt document
 */
export const downloadReceiptDocument = async (file: File, fileName: string) => {
  const url = window.URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return url;
};

/** * Delete a receipt document from S3
 * @param url - The S3 URL
 */
export const deleteReceiptDocumentFromS3 = (url: string) => {
  axios.delete(url);
};

/** * Download a receipt document from S3
 * @param presignedUrl - The presigned URL
 * @param fileName - The file name
 */

export const downloadReceiptDocumentFromS3 = async (presignedUrl: string, fileName: string) => {
  // Download the file using the presigned URL
  const downloadResponse = await axios.get(presignedUrl, {
    responseType: 'blob',
  });

  // Create a link element and trigger the download
  const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName); // You can set a custom filename here
  document.body.appendChild(link);
  link.click();
  link.remove();
};
